// Public Terms of Use — modernized for Roava 2026.
// PUBLIC: no auth.
// URL: https://roava.wgpapp.com/terms

import { createFileRoute, Link } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { MarketingShell } from "@/components/marketing/MarketingShell";

const EFFECTIVE_DATE = "June 2026";
const SUPPORT_EMAIL = "support@watsongrowthpartners.com";

export const Route = createFileRoute("/terms")({
  head: () =>
    pageMeta({
      title: "Terms of Use — Roava",
      description: "The terms that govern your use of Roava by WGP Apps.",
      path: "/terms",
    }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <MarketingShell>
      <article className="mx-auto max-w-2xl pt-2">
        <header className="mb-8">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">Legal</p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">Terms of Use</h1>
          <p className="mt-3 text-sm text-muted-foreground">Effective date: {EFFECTIVE_DATE}</p>
        </header>

        <div className="space-y-6 text-sm sm:text-[15px] leading-relaxed text-foreground/90">
          <Section title="Acceptance">
            <p>
              By using Roava (the "app"), you agree to these Terms of Use. If you do not
              agree, please do not use the app.
            </p>
            <p className="mt-3">
              Roava is built by{" "}
              <a
                href="https://wgpapp.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                WGP Apps
              </a>
              , part of the WGP brand family.
            </p>
          </Section>

          <Section title="Invite-only access">
            <p>
              Roava is an invite-only product for private trip groups. You may use the app
              only with a valid invite from a trip organizer.
            </p>
          </Section>

          <Section title="Acceptable use">
            <p>You agree not to:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>Use the app to harass, harm, or impersonate other people.</li>
              <li>Upload content you do not have the right to share.</li>
              <li>Attempt to reverse engineer, interfere with, or disrupt the service.</li>
              <li>Use the app for illegal activity.</li>
            </ul>
          </Section>

          <Section title="Not an emergency or safety service">
            <p>
              Roava provides route recording, trip visibility, and memory organization. It
              is <strong>not</strong> an emergency, rescue, or safety-monitoring service.
              Tracking and Family Follow features are not guaranteed real-time and must not
              be relied on for safety-critical purposes.
            </p>
          </Section>

          <Section title="Your travel decisions">
            <p>
              You are responsible for your own travel planning, navigation, and safety
              decisions. Roava is a coordination tool, not a substitute for proper trip
              planning, qualified guides, or local conditions.
            </p>
          </Section>

          <Section title="Your content">
            <p>
              You retain ownership of the photos, videos, route data, itinerary entries,
              and other content you contribute. By uploading content to a trip, you grant
              Roava the limited license needed to store, process, and display that content
              to the travelers and Family Follow viewers you have authorized, and to
              operate the service (for example, sync, backup, and reliability features).
            </p>
          </Section>

          <Section title="Optional in-app purchases">
            <p>
              Roava may offer optional in-app purchases to support development. These are
              not required to use the core app. Purchases are processed by Apple and
              subject to Apple's terms.
            </p>
          </Section>

          <Section title="No warranty">
            <p>
              The app is provided "as is" and "as available" without warranty of any kind,
              express or implied, including warranties of merchantability, fitness for a
              particular purpose, and non-infringement. We do not warrant that the app will
              be uninterrupted, error-free, or that data will always sync within a specific
              timeframe.
            </p>
          </Section>

          <Section title="Limitation of liability">
            <p>
              To the maximum extent permitted by law, WGP Apps and its affiliates are not
              liable for any indirect, incidental, special, consequential, or punitive
              damages arising out of your use of the app.
            </p>
          </Section>

          <Section title="Changes">
            <p>
              We may update these terms as the app evolves. Continued use of the app after
              an update constitutes acceptance of the new terms.
            </p>
          </Section>

          <Section title="Contact">
            <p>
              Questions about these terms? Email{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-primary hover:underline">
                {SUPPORT_EMAIL}
              </a>{" "}
              or visit{" "}
              <Link to="/support" className="text-primary hover:underline">
                Support
              </Link>
              .
            </p>
          </Section>
        </div>
      </article>
    </MarketingShell>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-lg sm:text-xl font-semibold tracking-tight mt-8 mb-3">{title}</h2>
      <div>{children}</div>
    </section>
  );
}
